import React from 'react';
import { Model } from '../types';
import { modelCategories, getModelIcon } from '../config/models';
import ContextStrip from './chat/ContextStrip';
import useKeyboardInset from '../hooks/useKeyboardInset';

interface ChatInputProps {
  theme: 'light' | 'dark';
  themeColors: any;
  isDragging: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  isPerplexityModel: (model: Model) => boolean;
  shouldShowBrowserIcon: (model: Model) => boolean;
  selectedModel: Model;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  inputMessage: string;
  setInputMessage: (message: string) => void;
  handleTextAreaHeight: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  getPlaceholderText: (model: Model) => string;
  isLoading: boolean;
  handleSubmit: (e: React.FormEvent, forcedModel?: Model, forcedMessage?: string) => Promise<void>;
  selectedFileName: string | null;
  selectedFile: File | null;
  selectedFiles?: { file: File; filename: string }[];
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setSelectedFile: (file: File | null) => void;
  setSelectedFileName: (name: string | null) => void;
  setSelectedFiles?: (files: { file: File; filename: string }[]) => void;
  isFileUploadEnabled: (model: Model) => boolean;
  showRightPanel?: boolean;
  showScrollToBottom: boolean;
  onScrollToBottom: () => void;
  isSidebarOpen?: boolean;
  // Width of the collapsed sidebar rail the composer must not sit under
  // (52 on desktop, 0 on mobile). Absent = 0, i.e. today's geometry.
  railOffset?: number;
  handleNewChat?: () => void;
  isLoadingConversation?: boolean;
  conversations?: any[];
  handleClipboardPaste: (e: ClipboardEvent) => Promise<void>;
  // Compare mode (multi-model parallel send) - optional, opt-in
  selectedModels?: Model[];
  setSelectedModels?: (models: Model[]) => void;
  // Inline composer error for workspace upload failures (403/413/network).
  uploadError?: string | null;
  onDismissUploadError?: () => void;
  // Untuk strip "Konteks · N file" di atas textarea (file workspace yang aktif
  // di percakapan ini). Absen = strip tidak dirender (perilaku lama).
  conversationId?: number | null;
  projectId?: number | null;
  // Mode Suara: tombol mic di baris ikon. Absen = tombol tidak dirender.
  onOpenVoice?: () => void;
}

// Middle-truncate: keep as much of the stem as fits (head + tail) and always
// preserve the extension, so "laporan-keuangan-kuartal-3-2026.xlsx" reads as
// "laporan-keuang…3-2026.xlsx" instead of losing the whole distinguishing tail.
const truncateFilename = (filename: string, maxLength: number = 24): string => {
  if (filename.length <= maxLength) return filename;
  const dot = filename.lastIndexOf('.');
  const ext = dot > 0 ? filename.slice(dot) : '';
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  const keep = maxLength - ext.length - 1; // 1 char reserved for the ellipsis
  if (keep < 4) return filename.slice(0, Math.max(1, maxLength - 1)) + '…';
  const head = Math.ceil(keep * 0.6);
  const tail = keep - head;
  return `${stem.slice(0, head)}…${tail > 0 ? stem.slice(stem.length - tail) : ''}${ext}`;
};

const ChatInput: React.FC<ChatInputProps> = ({
  theme,
  themeColors,
  isDragging,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  isPerplexityModel,
  shouldShowBrowserIcon,
  selectedModel,
  textareaRef,
  inputMessage,
  setInputMessage,
  handleTextAreaHeight,
  handleKeyPress,
  getPlaceholderText,
  isLoading,
  handleSubmit,
  selectedFileName,
  selectedFile,
  selectedFiles = [],
  handleFileSelect,
  setSelectedFile,
  setSelectedFileName,
  setSelectedFiles,
  isFileUploadEnabled,
  showRightPanel,
  showScrollToBottom,
  onScrollToBottom,
  isSidebarOpen,
  railOffset = 0,
  handleNewChat,
  isLoadingConversation,
  conversations,
  handleClipboardPaste,
  selectedModels,
  setSelectedModels,
  uploadError,
  onDismissUploadError,
  conversationId,
  projectId = null,
  onOpenVoice,
}) => {
  const compareActive = !!(selectedModels && selectedModels.length > 1);
  // Files-only sends are allowed: enable the send button when text OR at least
  // one attachment is present (composer attachments now go to the workspace).
  const canSend = (inputMessage.trim().length > 0 || selectedFiles.length > 0) && !isLoading;
  // Add useEffect to attach paste event listener
  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('paste', handleClipboardPaste);
      
      return () => {
        textarea.removeEventListener('paste', handleClipboardPaste);
      };
    }
  }, [textareaRef, handleClipboardPaste]);

  // railOffset === 0 means mobile layout (Chat passes isMobile ? 0 : 52).
  const isMobileLayout = railOffset === 0;
  // Desktop keeps the fixed overlay; the sidebar is an overlay on mobile and
  // must never offset the composer (2026-07 review).
  const sidebarInset = railOffset > 0 && isSidebarOpen ? '280px' : `${railOffset}px`;
  // Keyboard state still drives the bottom padding swap on mobile.
  const { inset: keyboardInset } = useKeyboardInset(isMobileLayout);
  return (
    <div style={{
      // MOBILE (2026-08-04 structural keyboard fix): STATIC footer of the
      // app-shell column. The shell's height is bound to the visual viewport,
      // so when the keyboard opens the composer rises above it IN LAYOUT and
      // Safari never pans the page. Fixed positioning survives on desktop.
      ...(isMobileLayout
        ? { position: 'static' as const, width: '100%' }
        : {
            position: 'fixed' as const,
            bottom: 0,
            left: sidebarInset,
            right: showRightPanel ? '320px' : '0',
            width: `calc(100% - ${sidebarInset} - ${showRightPanel ? '320px' : '0px'})`,
          }),
      padding: '14px 22px 12px',
      paddingBottom: keyboardInset > 0 ? '10px' : 'calc(12px + env(safe-area-inset-bottom))',
      background: theme === 'dark'
        ? 'linear-gradient(to top, #0A0B0D 62%, rgba(10,11,13,0))'
        : 'linear-gradient(to top, #F7F8FA 62%, rgba(247,248,250,0))',
      transition: 'all 0.3s ease',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <div
      className="pa-composer"
      style={{
        width: '100%',
        maxWidth: '700px',
        position: 'relative',
        background: themeColors[theme].composerBg,
        border: isDragging
          ? `1px dashed ${themeColors[theme].line2}`
          : `1px solid ${themeColors[theme].line2}`,
        borderRadius: '14px',
        padding: '13px',
        boxShadow: themeColors[theme].composerShadow,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        minWidth: 0,
        ['--pa-focus-ring' as any]: themeColors[theme].jade,
        ['--pa-focus-ring-dim' as any]: themeColors[theme].jadeDim,
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      >
        {compareActive && setSelectedModels && (() => {
          const total = selectedModels!.reduce((sum, mv) => {
            const m = modelCategories.flatMap((c) => c.models).find((mm) => mm.value === mv);
            const c = (m as any)?.credits;
            return sum + (typeof c === 'number' ? c : 0);
          }, 0);
          const hasUnlimitedOnly = total === 0;
          const removeModel = (mv: string) => {
            if (selectedModels!.length <= 1) return;
            setSelectedModels(selectedModels!.filter((v) => v !== mv) as Model[]);
          };
          return (
            <div style={{
              marginBottom: 10,
              paddingBottom: 10,
              borderBottom: `1px solid ${theme === 'light' ? '#F0EDE6' : '#2D2D2D'}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 9px',
                borderRadius: 999,
                background: theme === 'light' ? '#EEF2FF' : '#312E81',
                border: `1px solid ${theme === 'light' ? '#C7D2FE' : '#4338CA'}`,
                fontSize: 11,
                color: theme === 'light' ? '#4338CA' : '#C7D2FE',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontWeight: 600,
              }}>
                <span>🔀</span>
                <span>{selectedModels!.length} models</span>
                <span style={{ opacity: 0.5 }}>·</span>
                <span>{hasUnlimitedOnly ? 'free' : `~${total} credits`}</span>
              </div>
              {selectedModels!.map((mv) => {
                const meta = modelCategories.flatMap((c) => c.models).find((mm) => mm.value === mv);
                if (!meta) return null;
                return (
                  <div
                    key={mv}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '3px 6px 3px 5px',
                      borderRadius: 999,
                      background: theme === 'light' ? '#F8F6F1' : '#2A2A2A',
                      border: `1px solid ${theme === 'light' ? '#E5E7EB' : '#3A3A3A'}`,
                      fontSize: 11,
                      color: theme === 'light' ? '#1F1F1F' : '#FFFFFF',
                    }}
                  >
                    <img src={getModelIcon(meta.value, theme)} alt={meta.label} style={{ width: 12, height: 12 }} />
                    <span>{meta.label}</span>
                    <button
                      onClick={() => removeModel(mv)}
                      title="Remove from compare"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: theme === 'light' ? '#737373' : '#A3A3A3',
                        cursor: 'pointer',
                        padding: 0,
                        lineHeight: 1,
                        fontSize: 13,
                        marginLeft: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })()}
        {/* Konteks workspace yang aktif - terlihat di chat utama, bukan cuma
            di panel File kanan. */}
        {conversationId !== undefined && (
          <ContextStrip conversationId={conversationId} projectId={projectId} theme={theme} themeColors={themeColors} />
        )}
        {/* Attachment chips */}
        {isFileUploadEnabled(selectedModel) && selectedFiles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
            {selectedFiles.map((fileData, index) => (
              <div key={fileData.filename} style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 6px 3px 10px',
                background: themeColors[theme].panel2,
                border: `1px solid ${themeColors[theme].line}`,
                borderRadius: '999px', fontSize: '12px', color: themeColors[theme].ink2,
              }}>
                {truncateFilename(fileData.file.name)}
                <button
                  onClick={() => {
                    if (setSelectedFiles) {
                      const newFiles = selectedFiles.filter((_, i) => i !== index);
                      setSelectedFiles(newFiles);
                      if (newFiles.length === 0) { setSelectedFile(null); setSelectedFileName(null); }
                    }
                  }}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', color: themeColors[theme].ink3 }}
                >×</button>
              </div>
            ))}
          </div>
        )}

        {/* Inline upload error (workspace) - mirrors the File panel's error red */}
        {uploadError && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '6px',
            marginBottom: '8px', fontSize: '12px', lineHeight: 1.4,
            color: 'rgba(229,71,77,0.9)',
          }}>
            <span style={{ flex: 1, minWidth: 0 }}>{uploadError}</span>
            {onDismissUploadError && (
              <button
                onClick={onDismissUploadError}
                aria-label="Tutup pesan error"
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', color: 'rgba(229,71,77,0.9)', lineHeight: 1 }}
              >×</button>
            )}
          </div>
        )}

        <input type="file" id="file-upload" style={{ display: 'none' }} onChange={handleFileSelect} multiple accept="*/*" />

        <textarea
          ref={textareaRef}
          value={inputMessage}
          onChange={(e) => {
            setInputMessage(e.target.value);
            handleTextAreaHeight(e);
          }}
          onKeyPress={handleKeyPress}
          onFocus={() => {
            // Mobile: once the keyboard has settled, bring the latest message
            // back into view so focusing never leaves the thread mid-scroll.
            if (railOffset === 0) {
              window.setTimeout(() => onScrollToBottom(), 350);
            }
          }}
          className="pa-composer-input"
          style={{
            display: 'block',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: themeColors[theme].ink,
            fontSize: '14.5px',
            lineHeight: '1.5',
            resize: 'none',
            minHeight: '24px',
            maxHeight: '150px',
            width: '100%',
            padding: '1px 4px 13px',
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
          placeholder={
            compareActive
              ? `Tanya satu, ${selectedModels!.length} AI bakal jawab paralel...`
              : 'Tanya apa saja…'
          }
          rows={1}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '5px' }}>
            {isFileUploadEnabled(selectedModel) && (
              <>
                <label htmlFor="file-upload" className="pa-icon-ghost" title="Lampirkan file"
                  style={{ width: '29px', height: '29px', borderRadius: '8px', display: 'grid', placeItems: 'center', color: themeColors[theme].ink3, cursor: 'pointer' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21.4 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.2-9.19a4 4 0 015.65 5.66l-9.2 9.19a2 2 0 01-2.82-2.83l8.49-8.48"/></svg>
                </label>
                <label htmlFor="file-upload" className="pa-icon-ghost" title="Lampirkan gambar"
                  style={{ width: '29px', height: '29px', borderRadius: '8px', display: 'grid', placeItems: 'center', color: themeColors[theme].ink3, cursor: 'pointer' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                </label>
              </>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onOpenVoice && (
              <button type="button" onClick={onOpenVoice} className="pa-icon-ghost" title="Obrolan suara (live)"
                aria-label="Obrolan suara"
                style={{ width: '29px', height: '29px', borderRadius: '8px', display: 'grid', placeItems: 'center', color: themeColors[theme].ink3, cursor: 'pointer', background: 'transparent', border: 'none', padding: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14v-3a8 8 0 0 1 16 0v3"/><path d="M3 14h3a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Z"/><path d="M18 14h3a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1Z"/></svg>
              </button>
            )}
            {compareActive ? (
            <button
              onClick={handleSubmit}
              className="pa-send-btn"
              disabled={!inputMessage.trim() || isLoading}
              style={{
                background: themeColors[theme].btnPrimaryBg,
                color: themeColors[theme].btnPrimaryText,
                border: 'none',
                borderRadius: '9px',
                padding: '8px 14px',
                cursor: (!inputMessage.trim() || isLoading) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                opacity: (!inputMessage.trim() || isLoading) ? 0.5 : 1,
                transition: 'background 0.15s ease',
                ['--pa-btn-hover' as any]: themeColors[theme].btnPrimaryHover,
              }}
            >
              {isLoading ? 'Mengirim…' : `Compare ${selectedModels!.length} →`}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="pa-send-btn pa-send-icon"
              aria-label="Kirim pesan"
              title="Kirim"
              disabled={!canSend}
              style={{
                // pa-send-icon grows this to 36px below 768px (index.css).
                width: '32px', height: '32px', minWidth: '32px', borderRadius: '8px',
                background: !canSend ? themeColors[theme].panel2 : themeColors[theme].btnPrimaryBg,
                color: !canSend ? themeColors[theme].ink3 : themeColors[theme].btnPrimaryText,
                border: !canSend ? `1px solid ${themeColors[theme].line}` : 'none',
                cursor: !canSend ? 'not-allowed' : 'pointer',
                display: 'grid', placeItems: 'center', flexShrink: 0,
                transition: 'background 0.15s ease',
                ['--pa-btn-hover' as any]: themeColors[theme].btnPrimaryHover,
              }}
            >
              {isLoading ? (
                <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="28 50" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19V5" />
                  <path d="m5 12 7-7 7 7" />
                </svg>
              )}
            </button>
          )}
          </div>
        </div>
      </div>
      {/* Branding caption: desktop only. On mobile it sat between the
          composer and the keyboard and read as a dead gap. */}
      {!isMobileLayout && (
        <div style={{
          textAlign: 'center',
          fontFamily: themeColors[theme].mono,
          fontSize: '10.5px',
          letterSpacing: '0.02em',
          color: themeColors[theme].ink3,
          marginTop: '9px',
        }}>
          POWER AI · AUTO SEARCH &amp; IMAGE
        </div>
      )}
      {/* The scroll-to-bottom button lives in Chat.tsx (the real, styled
          40px circle). A content-less stub used to render here too and shipped
          as a UA-default grey box on mobile (2026-08-03 mobile review, P0). */}
    </div>
  );
};

export default React.memo(ChatInput); 